"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractBestFitAssetUrl = exports.extractAssetUrl = exports.cleanText = exports.slugify = void 0;
exports.normalizeTaxonomies = normalizeTaxonomies;
exports.convertToBuildKitTaxonomy = convertToBuildKitTaxonomy;
exports.convertTaxonomiesToBuildKitFormat = convertTaxonomiesToBuildKitFormat;
/**
 * Converts a string to a URL-friendly slug
 * Handles both plain strings and Builder.io localized values
 * @param str - The string to convert to a slug
 * @returns A URL-friendly slug string
 */
const slugify = (str) => {
    // Handle null/undefined
    if (!str)
        return '';
    // Extract string value from various formats
    let stringValue;
    if (typeof str === 'string') {
        stringValue = str;
    }
    else if (typeof str === 'object' && str !== null) {
        // Handle Builder.io LocalizedValue or similar objects
        stringValue = str.Default || str.default || '';
        if (!stringValue)
            return '';
    }
    else {
        stringValue = String(str);
    }
    return stringValue
        .replace(/\//g, "-")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "");
};
exports.slugify = slugify;
// Helper function to extract name from LocalizedValue or string
const extractName = (name) => {
    if (typeof name === "string")
        return name;
    if (name && typeof name === "object" && name.Default)
        return name.Default;
    return "";
};
// Helper for subcategories
function normalizeTaxonomyItems(raw) {
    return (raw || []).filter(Boolean).map((item) => ({
        name: item.name || { Default: "" },
        slug: item.slug || (0, exports.slugify)(extractName(item.name || "")),
        taxonomies: normalizeTaxonomyItems(item.taxonomies || []),
    }));
}
// Main function for top-level Taxonomy[]
function normalizeTaxonomies(raw) {
    return (raw || []).filter(Boolean).map((tax) => ({
        id: tax.id || "",
        name: tax.name || "",
        data: [
            {
                name: tax.data?.name || { Default: tax.name || "" },
                slug: tax.data?.slug ||
                    (0, exports.slugify)(extractName(tax.data?.name || tax.name || "")),
                taxonomies: normalizeTaxonomyItems(tax.data?.taxonomies || tax.taxonomies || []),
            },
        ],
        published: tax.published || false,
        createDate: tax.createDate || "",
        lastUpdatedDate: tax.lastUpdatedDate || "",
    }));
}
// Compatibility function to convert types package Taxonomy to build-kit Taxonomy format
function convertToBuildKitTaxonomy(taxonomy) {
    if (!taxonomy.data ||
        !Array.isArray(taxonomy.data) ||
        taxonomy.data.length === 0) {
        return {
            name: taxonomy.name || "",
            slug: "",
            taxonomies: [],
        };
    }
    const taxonomyData = taxonomy.data[0];
    function convertItem(item) {
        return {
            name: extractName(item.name),
            slug: item.slug || (0, exports.slugify)(extractName(item.name)),
            taxonomies: Array.isArray(item.taxonomies)
                ? item.taxonomies.map(convertItem)
                : [],
        };
    }
    return {
        name: extractName(taxonomyData.name),
        slug: taxonomyData.slug || (0, exports.slugify)(extractName(taxonomyData.name)),
        taxonomies: Array.isArray(taxonomyData.taxonomies)
            ? taxonomyData.taxonomies.map(convertItem)
            : [],
    };
}
// Convert array of types package Taxonomies to build-kit format
function convertTaxonomiesToBuildKitFormat(taxonomies) {
    return taxonomies.map(convertToBuildKitTaxonomy);
}
/**
 * Cleans text by removing special characters, invisible Unicode characters, and non-printable characters.
 * Useful for cleaning strings that may contain hidden formatting or control characters.
 *
 * @param text - The text to clean
 * @returns The cleaned text with only printable ASCII characters, or undefined if input is undefined
 */
const cleanText = (text) => {
    if (!text)
        return text;
    // Ensure the input is actually a string
    if (typeof text !== 'string') {
        return undefined;
    }
    return text
        .normalize("NFKD") // Decompose characters
        .replace(/[\u200B-\u200D\uFEFF\u2060-\u2064\u206A-\u206F\uFFF0-\uFFF8\uE000-\uF8FF]/g, "") // Remove various Unicode characters
        .replace(/[^\x20-\x7E]/g, "") // Keep only printable ASCII
        .trim();
};
exports.cleanText = cleanText;
/**
 * Extracts a URL from a DAM (Digital Asset Management) asset object or returns a string as-is.
 * Supports both legacy Builder.io string URLs and new Bynder asset objects.
 *
 * @param asset - The asset to extract URL from (can be a string URL or a Bynder asset object)
 * @param fallback - Optional fallback URL to return if no URL is found
 * @returns The extracted URL string or the fallback value
 */
const extractAssetUrl = (asset, fallback = "") => {
    if (!asset)
        return fallback;
    // If it's already a string, return it
    if (typeof asset === 'string')
        return asset;
    // If it's a DAMAsset object, try to extract the URL in order of preference
    if (typeof asset === 'object') {
        // Bynder format: additionalInfo.selectedFile.url
        if (asset.additionalInfo?.selectedFile?.url) {
            return asset.additionalInfo.selectedFile.url;
        }
        // Bynder format: assets[0].files.transformBaseUrl.url or webImage.url
        if (asset.assets?.[0]) {
            const firstAsset = asset.assets[0];
            if (firstAsset.files?.transformBaseUrl?.url) {
                return firstAsset.files.transformBaseUrl.url;
            }
            if (firstAsset.files?.webImage?.url) {
                return firstAsset.files.webImage.url;
            }
            if (firstAsset.files?.original?.url) {
                return firstAsset.files.original.url;
            }
        }
        // Legacy format fallbacks
        if (asset.assets?.none?.files?.none?.url) {
            return asset.assets.none.files.none.url;
        }
        // Direct url property
        if (asset.url) {
            return asset.url;
        }
    }
    return fallback;
};
exports.extractAssetUrl = extractAssetUrl;
/**
 * Calculates the difference between two sizes using aspect ratio and total area.
 * Returns a lower score for closer matches.
 */
const calculateSizeDifference = (targetWidth, targetHeight, actualWidth, actualHeight) => {
    const targetAspect = targetWidth / targetHeight;
    const actualAspect = actualWidth / actualHeight;
    const aspectDiff = Math.abs(targetAspect - actualAspect);
    const targetArea = targetWidth * targetHeight;
    const actualArea = actualWidth * actualHeight;
    const areaDiff = Math.abs(targetArea - actualArea) / targetArea;
    // Weight aspect ratio more heavily than area
    return aspectDiff * 2 + areaDiff;
};
/**
 * Extracts the best-fitting asset URL from a Bynder DAM object based on target dimensions.
 * Falls back to extractAssetUrl if no sized assets are available.
 *
 * @param asset - The asset to extract URL from (can be a string URL or a Bynder asset object)
 * @param targetWidth - Desired width in pixels
 * @param targetHeight - Desired height in pixels
 * @param fallback - Optional fallback URL to return if no URL is found
 * @returns The URL of the best-matching asset or the fallback value
 */
const extractBestFitAssetUrl = (asset, targetWidth, targetHeight, fallback = "") => {
    if (!asset)
        return fallback;
    // If it's already a string, return it
    if (typeof asset === 'string')
        return asset;
    // If it's not a Bynder object with files, use the basic extractor
    if (typeof asset !== 'object' || !asset.assets?.[0]?.files) {
        return (0, exports.extractAssetUrl)(asset, fallback);
    }
    const files = asset.assets[0].files;
    // Build a list of available sized assets with their dimensions
    const availableAssets = [];
    // Check all file variants
    for (const [key, value] of Object.entries(files)) {
        if (value && typeof value === 'object') {
            const file = value;
            // Skip if no URL
            if (!file.url)
                continue;
            let width = file.width;
            let height = file.height;
            // Try to parse dimensions from key if width/height not provided
            if ((!width || !height) && key.match(/^\d+x\d+$/)) {
                const [w, h] = key.split('x').map(Number);
                width = w;
                height = h;
            }
            // Skip if we don't have valid dimensions
            if (!width || !height || width <= 0 || height <= 0)
                continue;
            const score = calculateSizeDifference(targetWidth, targetHeight, width, height);
            availableAssets.push({
                url: file.url,
                width,
                height,
                score,
            });
        }
    }
    // If we found sized assets, return the best match
    if (availableAssets.length > 0) {
        availableAssets.sort((a, b) => a.score - b.score);
        return availableAssets[0].url;
    }
    // Fallback to the basic extractor
    return (0, exports.extractAssetUrl)(asset, fallback);
};
exports.extractBestFitAssetUrl = extractBestFitAssetUrl;
